import { onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'

/**
 * What a sheet closed by this composable wears on its `v-bottom-sheet`, so that the overlays opened
 * over it — a dialog, a menu, which Vuetify closes on back itself — are told from it.
 */
export const BACK_DISMISS_CLASS = 'app-back-dismiss'

const FOREIGN_OVERLAYS = `.v-overlay-container > .v-overlay--active:not(.v-tooltip):not(.${BACK_DISMISS_CLASS})`

function foreignOverlays(): Set<Element> {
  return new Set(document.querySelectorAll(FOREIGN_OVERLAYS))
}

// What is open, oldest first. Each one's guard runs on back, in the order they were added; only the
// newest closes.
const opened: object[] = []

/**
 * Closes something open — a sheet, a temporary drawer — on the browser's back button, Android's
 * included, and keeps the page under it (docs/plans/mobile-navigation.md, "Gestures, focus and
 * screen readers").
 *
 * Vuetify's own `closeOnBack` is not used: it keeps one flag for every overlay of the page,
 * `inTransition`, which stays set once any navigation skipped its `afterEach`, and every overlay
 * then lets back through. This listens to the router's history instead, which says a navigation
 * is a pop before any guard runs, and refuses that one navigation; the router puts the URL back.
 *
 * **Back closes what is on top.** Of several open, the newest only; and when a dialog or a menu
 * has opened over it since, that one, whose own guard Vuetify runs next — a dialog mounted with
 * the page has closed itself already, its guard being older.
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void) {
  const router = useRouter()
  const self = {}
  let popped = false
  let removers: Array<() => void> = []
  let below = new Set<Element>()

  function onTop(): boolean {
    if (opened.at(-1) !== self) return false
    for (const overlay of foreignOverlays()) if (!below.has(overlay)) return false
    return true
  }

  function listen() {
    opened.push(self)
    below = foreignOverlays()
    removers = [
      router.options.history.listen((_to, _from, info) => {
        popped = info.type === 'pop'
      }),
      router.beforeEach(() => {
        const back = popped
        popped = false
        if (back && isOpen() && onTop()) {
          close()
          return false
        }
        return undefined
      }),
      router.afterEach(() => {
        popped = false
      }),
    ]
  }

  function stop() {
    removers.forEach((remove) => remove())
    removers = []
    popped = false
    const index = opened.indexOf(self)
    if (index >= 0) opened.splice(index, 1)
  }

  // Only while it is open: nothing to hold otherwise.
  watch(isOpen, (open) => (open ? listen() : stop()), { immediate: true })
  onBeforeUnmount(stop)
}
